/** The Feedback list's view as URL search params: parsing, writing back, sorting. */

import type { FeedbackCategory, FeedbackListParams, FeedbackStatus } from '@/api/types';

export const DEFAULT_SORT = 'last_activity_at';
export const DEFAULT_PAGE_SIZE = 50;

/** Columns that open A to Z; the rest (dates) open newest first. */
const ASCENDING_FIRST = new Set(['title', 'author', 'category', 'status']);

/** Where a request's page links back to: this list, with its view kept. */
export interface FeedbackLinkState {
  from?: string;
}

export function parseFeedbackParams(search: URLSearchParams, canTriage: boolean): FeedbackListParams {
  return {
    scope: canTriage && search.get('scope') === 'all' ? 'all' : 'mine',
    statuses: search.getAll('status') as FeedbackStatus[],
    categories: search.getAll('category') as FeedbackCategory[],
    q: search.get('q') ?? '',
    sort: search.get('sort') ?? DEFAULT_SORT,
    order: search.get('order') === 'asc' ? 'asc' : 'desc',
    page: Math.max(1, Number(search.get('page')) || 1),
    page_size: Number(search.get('size')) || DEFAULT_PAGE_SIZE,
  };
}

/** The URL for `next`, with defaults left out. */
export function feedbackSearch(next: FeedbackListParams): URLSearchParams {
  const query = new URLSearchParams();
  if (next.scope === 'all') query.set('scope', 'all');
  next.statuses.forEach((s) => query.append('status', s));
  next.categories.forEach((c) => query.append('category', c));
  if (next.q) query.set('q', next.q);
  if (next.sort !== DEFAULT_SORT) query.set('sort', next.sort);
  if (next.order !== 'desc') query.set('order', next.order);
  if (next.page > 1) query.set('page', String(next.page));
  if (next.page_size !== DEFAULT_PAGE_SIZE) query.set('size', String(next.page_size));
  return query;
}

/** Anything but a page change starts again at page 1. */
export function applyPatch(params: FeedbackListParams, patch: Partial<FeedbackListParams>): FeedbackListParams {
  return { ...params, ...(patch.page === undefined ? { page: 1 } : {}), ...patch };
}

/** The same column flips its order; a new one opens in its natural order. */
export function sortPatch(params: FeedbackListParams, key: string): Partial<FeedbackListParams> {
  if (params.sort === key) return { order: params.order === 'asc' ? 'desc' : 'asc' };
  return { sort: key, order: ASCENDING_FIRST.has(key) ? 'asc' : 'desc' };
}
